import React, { useEffect, useState } from "react";
import CTAButton from "@/components/ui/CTAButton";
import { COLORS, FONTS, LAYOUT } from "@/constants/theme";

const NAV_LINKS = [
  { label: "Propuesta de valor", href: "#propuesta-valor" },
  { label: "Servicios", href: "#servicios" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Contacto", href: "#contacto" },
];

/**
 * Barra de navegación principal.
 *
 * @returns {JSX.Element}
 */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Escape cierra el panel: es lo que espera cualquiera que lo abra sin ratón.
  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const alPulsar = (evento) => {
      if (evento.key === "Escape") {
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
  }, [menuOpen]);

  return (
    <>
      {/* -- Navbar -- */}
      <header
        className="fixed left-0 top-0 z-50 w-full"
        style={{
          minHeight: LAYOUT.navHeight,
          backgroundColor: COLORS.blackHeader,
          borderBottom: scrolled ? `1px solid ${COLORS.borderSubtle}` : "1px solid transparent",
          transition: "border-color 200ms ease",
        }}
      >
        <div className="mx-auto h-[76px] px-6" style={{ maxWidth: LAYOUT.maxWidth }}>
          <div className="flex h-full items-center justify-between">
            <a
              aria-label="Ir al inicio de ALTAMA CONSULTING"
              className="inline-flex items-center"
              href="#inicio"
              style={{
                minHeight: "44px",
                color: COLORS.textPrimary,
                fontFamily: FONTS.display,
                fontSize: "1.42rem",
                fontWeight: 600,
                letterSpacing: "-0.005em",
                textDecoration: "none",
              }}
            >
              ALTAMA CONSULTING<span style={{ color: COLORS.digitalBlue }}>.</span>
            </a>

            <nav className="hidden items-center gap-9 md:flex">
              {NAV_LINKS.map((link) => (
                <a
                  className="nav-enlace"
                  href={link.href}
                  key={link.label}
                  style={{
                    color: COLORS.textPrimary,
                    fontFamily: FONTS.body,
                    fontSize: "0.98rem",
                    lineHeight: 1.2,
                  }}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="hidden md:block">
              <CTAButton href="#contacto" style={{ fontSize: "0.9rem", minWidth: "172px" }}>
                Agendar una reunión
              </CTAButton>
            </div>

            <button
              aria-label="Abrir menú de navegación"
              className="md:hidden"
              onClick={() => setMenuOpen(true)}
              style={{
                color: COLORS.textPrimary,
                backgroundColor: "transparent",
                border: `1px solid ${COLORS.borderMedium}`,
                borderRadius: "4px",
                width: "44px",
                height: "44px",
                cursor: "pointer",
              }}
              type="button"
            >
              <svg fill="none" height="18" viewBox="0 0 20 20" width="18">
                <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {menuOpen ? (
        <div className="fixed inset-0 z-[60] md:hidden">
          <button
            aria-label="Cerrar menú"
            className="absolute inset-0"
            onClick={() => setMenuOpen(false)}
            style={{ backgroundColor: COLORS.scrimOverlay, border: "none" }}
            type="button"
          />

          <aside
            className="absolute right-0 top-0 h-full w-[82%] max-w-[340px] p-6"
            style={{ backgroundColor: COLORS.bgSecondary, borderLeft: `1px solid ${COLORS.borderSubtle}` }}
          >
            <div className="mb-8 flex items-center justify-between">
              <span
                aria-label="ALTAMA CONSULTING"
                style={{
                  color: COLORS.textPrimary,
                  fontFamily: FONTS.display,
                  fontSize: "1.1rem",
                }}
              >
                ALTAMA CONSULTING<span style={{ color: COLORS.digitalBlue }}>.</span>
              </span>
              <button
                aria-label="Cerrar menú lateral"
                className="inline-flex items-center justify-center"
                onClick={() => setMenuOpen(false)}
                style={{
                  color: COLORS.textPrimary,
                  backgroundColor: "transparent",
                  border: `1px solid ${COLORS.borderMedium}`,
                  borderRadius: "4px",
                  width: "44px",
                  height: "44px",
                  marginRight: "-0.5rem",
                  cursor: "pointer",
                }}
                type="button"
              >
                <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 16 16" width="16">
                  <path
                    d="M3 3l10 10M13 3L3 13"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="1.8"
                  />
                </svg>
              </button>
            </div>

            <nav className="flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <a
                  href={link.href}
                  key={link.label}
                  onClick={() => setMenuOpen(false)}
                  style={{
                    color: COLORS.textSecondary,
                    fontFamily: FONTS.body,
                    fontSize: "1.05rem",
                    display: "flex",
                    alignItems: "center",
                    minHeight: "48px",
                    borderBottom: `1px solid ${COLORS.borderSubtle}`,
                  }}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <CTAButton className="mt-8" fullWidth href="#contacto" onClick={() => setMenuOpen(false)}>
              Agendar una reunión
            </CTAButton>
          </aside>
        </div>
      ) : null}
    </>
  );
};

export default Navbar;
